import {DOCUMENT} from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {AppMenuItem} from '../../compat/app-menu.model';
import {AppMenuService} from '../../compat/app-menu.service';
import {appMenuIconClass, filterVisibleMenuItems} from '../../compat/app-menu.utils';
import {AppSidebarService} from '../../prime/app-sidebar.service';
import {Subscription, combineLatest} from 'rxjs';

@Component({
	standalone: false,
	selector: 'app-menu',
	templateUrl: './app-menu.component.html',
	styleUrls: ['./app-menu.component.scss'],
})
export class AppMenuComponent implements OnInit, OnChanges, OnDestroy {
	@Input() items: AppMenuItem[] = [];
	@Input() autoCollapse = false;
	@Input() tag?: string;
	@Output() clickOutside = new EventEmitter<MouseEvent>();
	@ViewChild('flyout') flyout?: ElementRef<HTMLElement>;

	visibleItems: AppMenuItem[] = [];
	menuCompacted = false;
	expandedGroupIndex: number | null = null;
	floatingGroupIndex: number | null = null;
	flyoutTop = 0;
	flyoutLeft = 0;
	readonly menuIconClass = appMenuIconClass;

	private sidebarSub?: Subscription;

	constructor(
		private readonly menuService: AppMenuService,
		private readonly sidebarService: AppSidebarService,
		private readonly el: ElementRef<HTMLElement>,
		private readonly renderer: Renderer2,
		@Inject(DOCUMENT) private readonly document: Document,
	) {
	}

	ngOnInit(): void {
		this.sidebarSub = combineLatest([
			this.sidebarService.getSidebarState('menu-sidebar'),
			this.sidebarService.getHoverPeek(),
		]).subscribe(([state, hoverPeek]) => {
			this.menuCompacted = state === 'compacted' && !hoverPeek;
			if (!this.menuCompacted) {
				this.closeFlyout();
			}
		});
	}

	ngOnChanges(_changes: SimpleChanges): void {
		this.visibleItems = filterVisibleMenuItems(this.items || []);
	}

	ngOnDestroy(): void {
		this.sidebarSub?.unsubscribe();
	}

	get floatingGroup(): AppMenuItem | null {
		if (this.floatingGroupIndex === null) {
			return null;
		}
		return this.visibleItems[this.floatingGroupIndex] ?? null;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as Node;
		if (this.el.nativeElement.contains(target) || this.flyout?.nativeElement?.contains(target)) {
			return;
		}
		this.closeFlyout();
		this.clickOutside.emit(event);
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		this.closeFlyout();
	}

	@HostListener('window:scroll')
	onWindowScroll(): void {
		this.closeFlyout();
	}

	isGroupExpanded(index: number): boolean {
		return this.expandedGroupIndex === index;
	}

	isFlyoutOpen(index: number): boolean {
		return this.floatingGroupIndex === index;
	}

	isGroupActive(index: number): boolean {
		const item = this.visibleItems[index];
		if (!item?.children?.length) {
			return false;
		}
		return item.children.some((child) => this.isChildLinkActive(child, item.children));
	}

	toggleGroup(index: number, event: Event): void {
		event.preventDefault();
		event.stopPropagation();

		if (this.menuCompacted) {
			if (this.sidebarService.isTabletSidebarViewport()) {
				this.sidebarService.expand(this.tag || 'menu-sidebar');
				this.expandedGroupIndex = this.expandedGroupIndex === index ? null : index;
				this.closeFlyout();
				return;
			}
			if (this.floatingGroupIndex === index) {
				this.closeFlyout();
			} else {
				this.openFlyout(index, event.currentTarget as HTMLElement);
			}
			return;
		}

		this.expandedGroupIndex = this.expandedGroupIndex === index ? null : index;
	}

	onNavigate(item: AppMenuItem): void {
		this.closeFlyout();
		this.sidebarService.compact(this.tag || 'menu-sidebar');
		this.menuService.notifyClick({tag: this.tag, item});
	}

	onFlyoutNavigate(item: AppMenuItem): void {
		this.closeFlyout();
		this.onNavigate(item);
	}

	itemTooltip(title?: string): string | null {
		return this.menuCompacted && title ? title : null;
	}

	isChildLinkActive(child: AppMenuItem, siblings: AppMenuItem[] = []): boolean {
		return this.isLinkActive(child.link, siblings);
	}

	private openFlyout(index: number, anchor: HTMLElement): void {
		const rect = anchor.getBoundingClientRect();
		this.floatingGroupIndex = index;
		this.flyoutTop = Math.max(0.5, rect.top);
		this.flyoutLeft = rect.right + 6;
		setTimeout(() => this.attachFlyoutToBody());
	}

	private attachFlyoutToBody(): void {
		const flyoutEl = this.flyout?.nativeElement;
		if (!flyoutEl || flyoutEl.parentElement === this.document.body) {
			return;
		}
		this.renderer.appendChild(this.document.body, flyoutEl);
	}

	private closeFlyout(): void {
		this.floatingGroupIndex = null;
	}

	private isLinkActive(link?: string, siblings: AppMenuItem[] = []): boolean {
		if (!link || typeof window === 'undefined') {
			return false;
		}
		const path = window.location.pathname;
		if (path === link) {
			return true;
		}
		if (!path.startsWith(`${link}/`)) {
			return false;
		}

		const firstSegment = path.slice(link.length + 1).split('/')[0];
		const siblingRoutePrefixes = new Set(
			siblings
				.filter((item) => item.link && item.link !== link && item.link.startsWith(`${link}/`))
				.map((item) => item.link!.slice(link.length + 1).split('/')[0]),
		);

		return !siblingRoutePrefixes.has(firstSegment);
	}
}
